/**
 * O que a conclusão pode afirmar sobre a semana e sobre os erros, sem uma
 * segunda leitura.
 *
 * Os dias são os mesmos `AgendaDia` que `AgendaFita` desenha — a saída de
 * `agendaDePrevisao` sobre `GET /review/forecast`. Esta função não chama a
 * API e não soma a previsão de novo: a contagem de amanhã é o `total` daquele
 * dia. As duas telas mostram o mesmo número porque recebem o mesmo objeto.
 *
 * "Revisar os erros" não cabe em `GET /review/due` (não há filtro por id).
 * A entrega leva os ids, e a sessão de estudo monta a fila só com eles a
 * partir de `GET /review/cards`. "Antecipar amanhã" pede `GET /review/due`
 * até o fim daquele dia e fica só com os cartões cujo vencimento cai nele —
 * o limite cobre hoje e amanhã, senão o corte de 20 (ou só o total de
 * amanhã) devolve os de hoje e esvazia o dia seguinte.
 */
import type { AgendaDia } from "./home/revisaoHome";
import type { Rating, ReviewCard, SessionReinforceCard } from "../../../sdk/@types/Revisao";
import { cardTipoMostrado, type CardTipoMostrado } from "./cardTipo";
import { assuntoDoCartao } from "./painelLateral";

/** Fuso em que `GET /review/forecast` corta o dia. Sem horário de verão. */
const FUSO_PREVISAO = "America/Sao_Paulo";
const OFFSET_PREVISAO = "-03:00";

export interface CartaoDeReforco {
  id: string;
  /** Texto que identifica o cartão (`label` / frente). */
  rotulo: string;
  assunto: string;
  tipo: CardTipoMostrado;
}

export interface ConclusaoAcoesProps {
  /**
   * `total` do dia "Amanhã" na mesma fita. Zero esconde a ação — um botão
   * que abre uma sessão sem cartões não é oferecido.
   */
  antecipar: number;
  /** Ids dos cartões "errei", na ordem da lista. Vazio esconde a ação. */
  erros: readonly string[];
  onVoltar: () => void;
  onAntecipar: () => void;
  onRevisarErros: () => void;
}

/**
 * Estado de `/revisao/estudar` que a conclusão abre. Os campos que a tela de
 * montagem já manda (`dueBefore`, `limit`, `tags`) continuam os de
 * `RevisaoSessaoEntrega`. `cardIds` e `somenteDia` são desta tela.
 * `agenda` viaja junto para a conclusão seguinte não buscar a previsão de novo.
 */
export interface SessaoAbertaPelaConclusao {
  cardIds?: string[];
  somenteDia?: string;
  dueBefore?: string;
  limit?: number;
  tags?: string[];
  agenda?: AgendaDia[];
}

/** Um cartão da fila e a última nota vigente bastam para montar o reforço. */
export interface NotaParaReforco {
  cardId: string;
  rating: Rating;
  desfeita: boolean;
}

export function diaDeAmanha(dias: readonly AgendaDia[]): AgendaDia | undefined {
  return dias.find((dia) => dia.label === "Amanhã");
}

/** Zero quando a fita não tem amanhã ou o total daquele dia não é positivo. */
export function contagemDeAmanha(dias: readonly AgendaDia[]): number {
  const amanha = diaDeAmanha(dias);
  if (!amanha || !Number.isFinite(amanha.total) || amanha.total <= 0) return 0;
  return amanha.total;
}

/**
 * Fim do dia civil da previsão, no mesmo corte de `getReviewForecast`
 * (23:59:59.999 em America/Sao_Paulo). `dueBefore` da API é `$lte` esse instante.
 */
export function fimDoDiaPrevisao(iso: string): string {
  return `${iso}T23:59:59.999${OFFSET_PREVISAO}`;
}

/** `yyyy-MM-dd` do instante no fuso da previsão. Vazio se a data não parseia. */
export function diaDaPrevisao(instant: string): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: FUSO_PREVISAO,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Sessão com os cartões que a previsão já contou para amanhã.
 * `null` quando não há o que antecipar — a ação não é oferecida.
 */
export function entregaAnteciparAmanha(
  dias: readonly AgendaDia[],
): SessaoAbertaPelaConclusao | null {
  const amanha = diaDeAmanha(dias);
  const total = contagemDeAmanha(dias);
  if (!amanha || total <= 0) return null;
  const ateAmanha = dias.filter((dia) => dia.iso <= amanha.iso);
  const limiteDaJanela = ateAmanha.reduce((sum, dia) => sum + (Number.isFinite(dia.total) ? dia.total : 0), 0);
  return {
    dueBefore: fimDoDiaPrevisao(amanha.iso),
    limit: Math.max(limiteDaJanela, total),
    somenteDia: amanha.iso,
    agenda: [...dias],
  };
}

/** Sessão cuja fila é exatamente esses ids, nesta ordem. */
export function entregaRevisarErros(
  ids: readonly string[],
  agenda?: readonly AgendaDia[],
): SessaoAbertaPelaConclusao {
  return {
    cardIds: [...ids],
    ...(agenda ? { agenda: [...agenda] } : {}),
  };
}

/**
 * Lista visível do reforço. Vazia quando a leitura não trouxe erro — a seção
 * não se desenha. Assunto e tipo saem dos mesmos lugares que o painel da
 * sessão já usa (`assuntoDoCartao`, `cardTipoMostrado`).
 */
export function listaDeReforco(
  cards: readonly SessionReinforceCard[] | undefined,
  assuntos: Readonly<Record<string, string>> = {},
): CartaoDeReforco[] {
  if (!cards || cards.length === 0) return [];
  const lista: CartaoDeReforco[] = [];
  for (const card of cards) {
    if (typeof card.id !== "string" || card.id === "") continue;
    const rotulo = card.label.trim();
    lista.push({
      id: card.id,
      rotulo: rotulo || "Cartão",
      assunto: assuntoDoCartao(
        { id: card.id, subjectId: card.subjectId, layout: card.layout },
        assuntos,
      ),
      tipo: cardTipoMostrado({ layout: card.layout }),
    });
  }
  return lista;
}

/**
 * Cartões cuja última nota vigente nesta sessão foi "again". Uma nota
 * desfeita não conta; um "bom" depois de um "errei" tira o cartão da lista.
 * A ordem é a da fila, não a ordem em que as notas foram dadas.
 */
export function reforcoDasNotas(
  fila: readonly Pick<ReviewCard, "id" | "front" | "subjectId" | "layout" | "shortId">[],
  notas: readonly NotaParaReforco[],
): SessionReinforceCard[] {
  const ultima = new Map<string, Rating>();
  for (const nota of notas) {
    if (nota.desfeita) continue;
    ultima.set(nota.cardId, nota.rating);
  }
  const cards: SessionReinforceCard[] = [];
  for (const card of fila) {
    if (ultima.get(card.id) !== "again") continue;
    const item: SessionReinforceCard = {
      id: card.id,
      label: card.front?.trim() || "Cartão",
      subjectId: card.subjectId,
    };
    if (card.layout) item.layout = card.layout;
    if (card.shortId !== undefined) item.shortId = card.shortId;
    cards.push(item);
  }
  return cards;
}

/** Mantém, na ordem pedida, os cartões cujo vencimento cai naquele dia da previsão. */
export function cartoesDoDia(
  cards: readonly ReviewCard[],
  iso: string,
): ReviewCard[] {
  return cards.filter((card) => diaDaPrevisao(card.dueDate) === iso);
}

/** Mantém, na ordem dos ids, os cartões que a listagem devolveu. Não inventa os que faltam. */
export function cartoesPorIds(
  cards: readonly ReviewCard[],
  ids: readonly string[],
): ReviewCard[] {
  const byId = new Map(cards.map((card) => [card.id, card]));
  const fila: ReviewCard[] = [];
  for (const id of ids) {
    const card = byId.get(id);
    if (card) fila.push(card);
  }
  return fila;
}
